<template>
  <c-box class="product-return-list">
    <c-box>
      <RMAReturnSearch
        :searchCallback="searchCallback"
        v-chakra
        :mt="{ base: 'xl', md: '4xl' }"
        :mb="{ base: 'xl', md: '4xl' }"
      />

      <c-box
        :display="{ base: 'none', md: 'grid' }"
        align-items="start"
        py="xs"
        border-bottom="1px solid"
        border-color="gray.300"
        grid-template-columns="2fr 1fr 2fr 3fr 1fr 2fr 4fr"
        grid-auto-rows="minmax(1.75rem, auto)"
        gap="10px"
        color="deepBlack"
      >
        <c-box>{{ $t("productReturnV2.header.created") }}</c-box>
        <c-box>{{ $t("productReturnV2.header.id") }}</c-box>
        <c-box>{{ $t("productReturnV2.header.type") }}</c-box>
        <c-box>{{ $t("productReturnV2.header.customers") }}</c-box>
        <c-box>{{ $t("productReturnV2.header.quantity") }}</c-box>
        <c-box>{{ $t("productReturnV2.header.replacementOrdered") }}</c-box>
        <c-box>{{ $t("common.status") }}</c-box>
      </c-box>

      <c-box v-if="filteredReturnListing.length > 0" :pb="{ base: '4xl', md: 0 }">
        <c-box
          v-for="(a, index) in filteredReturnListing[currentPage - 1]"
          :key="index"
          :py="{ base: 'xs', md: 'xl' }"
          @click="!a.draft && showReturnDetails(a)"
          @keydown.enter="!a.draft && showReturnDetails(a)"
          :cursor="!a.draft && 'pointer'"
          tabindex="0"
          :display="{ md: 'grid' }"
          grid-template-columns="2fr 1fr 2fr 3fr 1fr 2fr 4fr"
          grid-auto-rows="minmax(40px, auto)"
          gap="10px"
          align-items="normal"
          border-bottom="1px solid"
          border-bottom-color="gray.300"
          v-chakra="{
            '&:first-of-type': {
              borderTop: { base: '1px solid', md: 'none' },
              borderTopColor: 'gray.300',
            },
          }"
        >
          <!-- Created -->
          <c-box
            display="grid"
            :grid-template-columns="{ base: 'auto auto', md: '15px auto', xl: '30px auto' }"
            :grid-template-areas="{
              base: `
                'label date'
                '. name'
              `,
              md: `
                'arrow date'
                '. name'
              `,
            }"
            grid-template-rows="1.5rem auto"
          >
            <c-box :display="{ md: 'none' }" font-weight="bold" grid-area="label">
              {{ $t("productReturnV2.header.created") }}
            </c-box>
            <c-box :display="{ base: 'none', md: 'contents' }">
              <!-- For some reason using mt="0.25rem" in this component results weirdness but defining that value with v-chakra works... -->
              <c-icon
                name="chevron_right"
                size="0.8rem"
                color="brand.purpleDark"
                :display="{ base: 'none', md: 'inherit' }"
                grid-area="arrow"
                justify-self="start"
                mx="0"
                mb="0"
                py="0"
                px="0"
                v-chakra="{ mt: '0.25rem' }"
              />
              <!--  -->
            </c-box>
            <c-box
              :title="
                $t('productReturn.columnTitles.created', {
                  dateAndTime: moment(a.created).format('D.M.YYYY HH:mm:ss'),
                })
              "
              color="brand.purple"
              grid-area="date"
              :text-align="{ base: 'right', md: 'left' }"
            >
              {{ moment(a.created) | moment("D.M.YYYY") }}
            </c-box>

            <!-- Sales and campaign return -->
            <c-box
              v-if="a.productReturnData && a.productReturnData.contactDetails"
              :title="a.productReturnData.contactDetails.fullName"
              grid-area="name"
              font-size="xs"
              :text-align="{ base: 'right', md: 'left' }"
              overflow="hidden"
              white-space="nowrap"
              text-overflow="ellipsis"
              color="gray.600"
            >
              <span>
                {{ a.productReturnData.contactDetails.fullName }}
              </span>
            </c-box>
            <!-- Maintenance return -->
            <c-box
              v-else-if="a.contactDetails && a.contactDetails.fullName"
              :title="a.contactDetails.fullName"
              grid-area="name"
              font-size="xs"
              :text-align="{ base: 'right', md: 'left' }"
              overflow="hidden"
              white-space="nowrap"
              text-overflow="ellipsis"
              color="gray.600"
            >
              {{ a.contactDetails.fullName }}
            </c-box>
          </c-box>

          <c-flex
            :title="$t('productReturnV2.columnTitles.returnId', { id: a.rmaId })"
            justify-content="space-between"
            color="brand.purple"
          >
            <c-box :display="{ md: 'none' }" font-weight="bold" grid-area="label" color="black">
              {{ $t("productReturnV2.header.id") }}
            </c-box>
            <span>{{ getReturnId(a) }}</span>
          </c-flex>

          <!-- Return type -->
          <c-flex :title="$t('productReturnV2.columnTitles.type')" justify-content="space-between">
            <c-box :display="{ md: 'none' }" font-weight="bold">
              {{ $t("productReturnV2.header.type") }}
            </c-box>

            <span v-if="getReturnType(a)">{{
              $t(`productReturnV2.returnType.${getReturnType(a)}`)
            }}</span
            ><span v-else>-</span>
          </c-flex>

          <!-- Customers -->
          <c-flex justify-content="space-between">
            <c-box :display="{ md: 'none' }" font-weight="bold" grid-area="label">
              {{ $t("productReturnV2.header.customers") }}
            </c-box>
            <span>{{ listReturnCustomers(a) }}</span>
          </c-flex>

          <!-- qty -->
          <c-box :my="{ base: '0.25rem', md: '0' }">
            <tag :quantity="getReturnQty(a)" :label="String($t('common.qty'))" />
          </c-box>

          <!-- replace ordered -->
          <c-box :my="{ base: '0.25rem', md: '0' }">
            <tag
              :quantity="getReturnReplacedQty(a)"
              :label="String($t('productReturnV2.header.replacementOrdered'))"
              :use-color="true"
            />
          </c-box>

          <!-- Status -->
          <c-box
            :display="{ base: 'flex', md: 'grid' }"
            justify-content="space-between"
            gap="10px"
            :grid-template-columns="{
              base: '1fr 2fr 2fr',
              md: '1fr 2fr',
            }"
          >
            <c-box :display="{ md: 'none' }" font-weight="bold" grid-area="label">
              {{ $t("common.status") }}
            </c-box>

            <!-- Return sent content -->
            <c-box display="contents">
              <c-box
                :title="$t('productReturnV2.columnTitles.status')"
                v-chakra="{
                  '.return-progress > div': {
                    backgroundColor: 'brand.purple',
                  },
                  '.FINISHED > div': {
                    backgroundColor: 'brand.green',
                  },
                  '.CANCELLED > div': {
                    backgroundColor: 'brand.redLight',
                  },
                  '.UNKNOWN > div': {
                    backgroundColor: 'brand.purpleLight',
                  },
                }"
              >
                <c-progress
                  v-if="isMaintenanceReturn(a)"
                  class="return-progress"
                  :class="a.status"
                  :value="a.progress"
                  border-radius="full"
                  size="lg"
                  background="gray.200"
                />
                <c-progress
                  v-else
                  class="UNKNOWN"
                  has-stripe
                  :value="100"
                  border-radius="full"
                  size="lg"
                  background="gray.200"
                />
              </c-box>

              <c-box
                v-if="isMaintenanceReturn(a)"
                :title="$t('productReturnV2.columnTitles.status')"
                color="gray.600"
              >
                <c-box v-if="a.status">
                  {{ $t(`productReturnV2.returnStatus.${a.status}`) }}
                </c-box>
                <c-box v-else>
                  {{ $t(`productReturnV2.details.status.UNKNOWN`) }}
                </c-box>
                <c-box v-if="a.status">
                  <c-box v-if="quotationSentForSomeLine(a)">
                    <c-icon name="alert" size="1.4rem" color="brand.orange" />
                    {{ $t("productReturnV2.warning.REPAIR_QUOTATION_SENT_TO_CUSTOMER") }}
                  </c-box>
                </c-box>
              </c-box>
              <c-box
                v-else-if="isSalesReturn(a)"
                :title="$t('productReturnV2.columnTitles.status')"
                color="gray.600"
              >
                {{ $t("productReturn.note.salesReturnStatusesMissing") }}
              </c-box>
              <c-box
                v-else-if="isCampaignReturn(a)"
                :title="$t('productReturnV2.columnTitles.status')"
                color="gray.600"
              >
                {{ $t("productReturn.note.campaignReturnStatusesMissing") }}
              </c-box>
              <c-box
                v-else
                :title="$t('productReturn.columnTitles.combinedStatus')"
                color="gray.600"
              >
                {{ $t("productReturn.note.oldReturnStatusesMissing") }}
              </c-box>
            </c-box>
          </c-box>
        </c-box>
        <pagination
          :totalPages="filteredReturnListing.length"
          :perPage="returnsPerPage"
          :currentPage="currentPage"
          @pageChanged="onPageChange"
        />
      </c-box>
      <c-box v-else mt="xl">
        {{ $t("productReturnV2.pastReturns.search.noReturnsFound") }}
      </c-box>
    </c-box>
  </c-box>
</template>

<script lang="ts">
import Pagination from "@/components/Pagination.vue";
import Tag from "@/components/returns/Tag.vue";
import Button from "@/design-system/elements/Button.vue";
import { CBox, CFlex, CIcon, CProgress, CText } from "@chakra-ui/vue";
import {
  getCroppedReturnId,
  ICampaignReturnResult,
  IMaintenanceReturnResult,
  IResultEntity,
  ISalesReturnResult,
  maintenanceReturnQty,
  maintenanceReturnReplacedQty,
  quotationSentForSomeLine,
  ReturnType,
  salesOrCampaignReturnQty,
  IRootState,
} from "@iloq/shared";
import { defineComponent, PropType } from "vue";
import { mapState } from "vuex";
import { productReturnDataMixin } from "./RMAMixins";
import RMAReturnSearch from "./RMAReturnSearch.vue";

export default defineComponent({
  name: "RMAReturnList",
  mixins: [productReturnDataMixin],
  components: {
    Button,
    RMAReturnSearch,
    Pagination,
    CText,
    CBox,
    CIcon,
    Tag,
    CProgress,
    CFlex,
  },
  props: {
    returnListing: {
      type: Array as PropType<IResultEntity[]>,
      required: true,
    },
  },
  data() {
    return {
      ReturnType,
      moment: (this as any).$moment,
      // filteredReturnListing: [] as IResultEntity[][],
      // TODO: using any to get rid of typing issues, which are irrevelant atm
      filteredReturnListing: [] as any[][],
      currentPage: 1,
      returnsPerPage: 10,
    };
  },
  computed: {
    ...mapState({
      user: (state: unknown) => (state as IRootState).user!,
    }),
  },
  mounted() {
    this.filteredReturnListing = this.splitReturnsToPages(this.returnListing, this.returnsPerPage);
  },
  methods: {
    splitReturnsToPages(returns: IResultEntity[], itemsPerPage: number): IResultEntity[][] {
      const pages: IResultEntity[][] = [];
      for (let i = 0; i < returns.length; i += itemsPerPage) {
        const page = returns.slice(i, i + itemsPerPage);
        pages.push(page);
      }
      return pages;
    },
    searchCallback(searchTerm: string) {
      if (searchTerm) {
        const term = searchTerm.toLowerCase();
        const filteredReturns = this.returnListing.filter(
          (productReturn: IResultEntity | IMaintenanceReturnResult) => {
            const sites = (productReturn as IMaintenanceReturnResult)?.productDetails?.sites;
            const rmaIdMatch = productReturn?.rmaId?.toLowerCase().includes(term);

            const customerNameMatch = sites?.some((site) =>
              site?.siteDetails?.name?.toLowerCase().includes(term),
            );
            // serviceItemNumber aka crNumber
            const serviceItemNumberMatch = sites?.some((site) =>
              site.products?.some((product) =>
                product.instances?.some(({ serviceItemNumber }) =>
                  serviceItemNumber?.toLowerCase().includes(term),
                ),
              ),
            );
            return rmaIdMatch || customerNameMatch || serviceItemNumberMatch;
          },
        );
        this.filteredReturnListing = this.splitReturnsToPages(filteredReturns, this.returnsPerPage);
      } else {
        this.filteredReturnListing = this.splitReturnsToPages(
          this.returnListing,
          this.returnsPerPage,
        );
      }
      // reset the current page
      this.currentPage = 1;
    },
    isMaintenanceReturnWithStatus(
      data: ISalesReturnResult | ICampaignReturnResult | IMaintenanceReturnResult,
    ): boolean {
      return this.isMaintenanceReturn(data) && !!(data as IMaintenanceReturnResult).status;
    },
    showReturnDetails(
      data: IResultEntity | ISalesReturnResult | ICampaignReturnResult | IMaintenanceReturnResult,
    ) {
      if (this.isSalesReturn(data)) {
        this.$router.push({ path: `/support/returns/sales_return/${data.rmaId}` });
      } else if (this.isMaintenanceReturn(data)) {
        this.$router.push({ path: `/support/returns/maintenance_return/${data.rmaId}` });
      } else if (this.isCampaignReturn(data)) {
        this.$router.push({ path: `/support/returns/campaign_return/${data.rmaId}` });
      } else {
        this.$router.push({ path: `/support/returns/old_return/${data.rmaId}` });
      }
    },
    getReturnId({ rmaId, draft }: IResultEntity): string {
      if (draft) {
        return this.$t("productReturn.common.draft").toString().toUpperCase();
      }
      return getCroppedReturnId(rmaId);
    },
    listReturnCustomers(data: IMaintenanceReturnResult): string {
      if (!this.isMaintenanceReturn(data)) {
        return "-";
      }

      const siteNames = (data.productDetails?.sites || []).map((site) => site.siteDetails.name);

      if (siteNames.includes("")) {
        siteNames.push(this.$t("productReturnV2.site.nameUnknown").toString());
      }

      const customerNames = siteNames
        // filter out possible empty site names
        .filter((site) => site)
        .join(", ");
      return customerNames || "-";
    },
    getReturnQty(
      data: ISalesReturnResult | ICampaignReturnResult | IMaintenanceReturnResult,
    ): string {
      const qty = this.isMaintenanceReturn(data)
        ? maintenanceReturnQty(data as IMaintenanceReturnResult)
        : salesOrCampaignReturnQty(data as ISalesReturnResult | ICampaignReturnResult);

      return qty === 0 ? "-" : qty.toString();
    },
    getReturnReplacedQty(data: IMaintenanceReturnResult): string {
      const qty = this.isMaintenanceReturn(data) ? maintenanceReturnReplacedQty(data) : 0;

      return qty === 0 ? "-" : qty.toString();
    },
    quotationSentForSomeLine(data: IMaintenanceReturnResult): boolean {
      return quotationSentForSomeLine(data);
    },
    deleteDraft(data: IResultEntity): void {
      this.$store.dispatch("returns/deleteDraft", data.id);
    },
    onPageChange(page: number) {
      this.currentPage = page;
    },
  },
});
</script>
